import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Play, Pause, Heart, Download, DollarSign, Clock, Music, ShoppingCart, ExternalLink, Volume2, VolumeX, SkipBack, SkipForward, Share2, Check, Filter, ChevronDown, Activity, X, List, LayoutGrid, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');

const PublicBeatStore = () => {
  const { storeId } = useParams(); // This is the user_id of the store owner
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Default to grid view; persist user's chosen preference in localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('beatStoreViewMode') || 'grid';
    }
    return 'grid';
  }); // 'list' or 'grid'
  
  // Filters
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [selectedMood, setSelectedMood] = useState('All');
  const [selectedBpmRange, setSelectedBpmRange] = useState('All');
  const [selectedPrice, setSelectedPrice] = useState('All');
  const [selectedDateSort, setSelectedDateSort] = useState('Default');
  const [showFilters, setShowFilters] = useState(false);
  
  // Terms and Conditions
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  const audioRef = useRef(null);

  // Filter options
  const genres = ['All', 'Hip Hop', 'R&B', 'Pop', 'EDM', 'Rock', 'Jazz', 'Trap', 'Lo-Fi', 'Soul', 'House', 'Metal', 'Country', 'Rage', 'Afro Beat', 'Break Beat'];
  const moods = ['All', 'Dark', 'Happy', 'Sad', 'Energetic', 'Chill', 'Aggressive', 'Romantic', 'Mysterious'];
  const bpmRanges = ['All', '60-90', '90-120', '120-150', '150-180', '180+'];
  const priceRanges = ['All', 'Free', 'Under $25', '$25-$50', '$50-$100', 'Over $100'];
  const dateSorts = ['Default', 'Newest First', 'Oldest First'];

  useEffect(() => {
    fetchTracks();
    fetchStoreInfo();
  }, [storeId]);

  const fetchTracks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/audio-storage/tracks?user_id=${storeId}`);
      if (response.ok) {
        const data = await response.json();
        setTracks(data.tracks || []);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/audio-storage/store/${storeId}`);
      if (response.ok) {
        const data = await response.json();
        setStoreInfo(data);
      }
    } catch (error) {
      console.error('Error fetching store info:', error);
    }
  };

  // Audio player functions
  const playTrack = (track) => {
    if (currentTrack?.id === track.id) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      setCurrentTrack(track);
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    if (currentTrack && audioRef.current) {
      audioRef.current.src = `${API_URL}${currentTrack.audio_url}`;
      audioRef.current.volume = isMuted ? 0 : volume;
      audioRef.current.play().catch(console.error);
    }
  }, [currentTrack]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const playNext = () => {
    const currentIndex = filteredTracks.findIndex(t => t.id === currentTrack?.id);
    if (currentIndex < filteredTracks.length - 1) {
      setCurrentTrack(filteredTracks[currentIndex + 1]);
      setIsPlaying(true);
    }
  };

  const playPrevious = () => {
    const currentIndex = filteredTracks.findIndex(t => t.id === currentTrack?.id);
    if (currentIndex > 0) {
      setCurrentTrack(filteredTracks[currentIndex - 1]);
      setIsPlaying(true);
    }
  };

  // Cart functions
  const addToCart = (track) => {
    if (!cart.find(t => t.id === track.id)) {
      setCart([...cart, track]);
      toast.success(`Added "${track.title}" to cart`);
    } else {
      toast.info('Track already in cart');
    }
  };

  const removeFromCart = (trackId) => {
    setCart(cart.filter(t => t.id !== trackId));
  };

  const getCartTotal = () => {
    return cart.reduce((total, track) => total + (track.price || 0), 0);
  };

  // Download a track (for free beats or after purchase)
  const downloadTrack = async (track) => {
    try {
      toast.loading(`Preparing download for "${track.title}"...`, { id: 'download' });
      
      // Get the audio file URL
      const audioUrl = track.audio_url 
        ? `${API_URL}${track.audio_url}` 
        : track.file_id 
          ? `${API_URL}/api/audio-storage/stream/${track.file_id}`
          : null;
      
      if (!audioUrl) {
        toast.error('Download not available for this track', { id: 'download' });
        return;
      }
      
      // Fetch the file
      const response = await fetch(audioUrl);
      if (!response.ok) throw new Error('Failed to fetch audio file');
      
      const blob = await response.blob();
      
      // Determine file extension from original filename or default to mp3
      let extension = 'mp3';
      if (track.original_filename) {
        const parts = track.original_filename.split('.');
        if (parts.length > 1) extension = parts.pop();
      } else if (track.audio_url && track.audio_url.includes('.')) {
        const urlParts = track.audio_url.split('/').pop().split('.');
        if (urlParts.length > 1 && urlParts[urlParts.length - 1].length <= 4) {
          extension = urlParts.pop();
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${track.title || 'beat'}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded "${track.title}"!`, { id: 'download' });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download track', { id: 'download' });
    }
  };

  // Checkout - handles payment for paid tracks
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
  const [purchasedTracks, setPurchasedTracks] = useState([]);

  const handleCheckout = async () => {
    // Separate free and paid tracks
    const freeTracks = cart.filter(t => t.is_free || !t.price || t.price === 0);
    const paidTracks = cart.filter(t => !t.is_free && t.price && t.price > 0);
    
    // If all tracks are free, just download them
    if (paidTracks.length === 0) {
      for (const track of freeTracks) {
        await downloadTrack(track);
      }
      setCart([]);
      setShowCart(false);
      return;
    }
    
    // For paid tracks, initiate checkout
    setCheckoutLoading(true);
    try {
      const total = getCartTotal();
      
      // Create checkout session - backend handles URL construction
      const response = await fetch(`${API_URL}/api/audio-storage/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: paidTracks.map(t => ({
            id: t.id,
            title: t.title,
            price: t.price,
            artist: t.artist
          })),
          store_id: storeId,
          origin_url: window.location.origin
        })
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else if (data.error === 'stripe_not_configured') {
        // Stripe not configured - simulate purchase for demo
        toast.success(`Demo purchase successful! Total: $${total.toFixed(2)}`);
        setPurchasedTracks([...purchasedTracks, ...cart.map(t => t.id)]);
        
        // Download free tracks and "purchased" tracks
        for (const track of cart) {
          await downloadTrack(track);
        }
        setCart([]);
        setShowCart(false);
        setShowCheckoutSuccess(true);
      } else {
        throw new Error(data.detail || 'Checkout failed');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      // On error, only download free tracks - don't give away paid tracks
      const freeTracks = cart.filter(t => t.is_free || !t.price || t.price === 0);
      if (freeTracks.length > 0) {
        toast.info(`Checkout unavailable. Downloading ${freeTracks.length} free track(s)...`);
        for (const track of freeTracks) {
          await downloadTrack(track);
        }
        // Remove only free tracks from cart
        setCart(cart.filter(t => !t.is_free && t.price && t.price > 0));
      } else {
        toast.error('Checkout temporarily unavailable. Please try again later.');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.info('Payment verification timed out. Please check your email for confirmation.');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/audio-storage/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        toast.success('Payment confirmed! Your beats are ready for download.');
        setPurchasedTracks([...purchasedTracks, ...(data.item_ids || [])]);
        setShowCheckoutSuccess(true);
        setCart([]);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }
      
      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  // Check for checkout success on URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (params.get('checkout') === 'success' && sessionId) {
      // Poll payment status to confirm and get purchased items
      pollPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('checkout') === 'success') {
      setShowCheckoutSuccess(true);
      toast.success('Payment successful! Your beats are ready for download.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('checkout') === 'cancelled') {
      toast.info('Checkout cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Share link
  const copyShareLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Filter tracks
  const filteredTracks = tracks.filter(track => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = track.title?.toLowerCase().includes(query);
      const matchesArtist = track.artist?.toLowerCase().includes(query);
      const matchesTags = track.tags?.some(tag => tag.toLowerCase().includes(query));
      if (!matchesTitle && !matchesArtist && !matchesTags) return false;
    }
    
    // Genre filter
    if (selectedGenre !== 'All' && track.genre !== selectedGenre) return false;
    
    // Mood filter
    if (selectedMood !== 'All' && track.mood !== selectedMood) return false;
    
    // BPM filter
    if (selectedBpmRange !== 'All' && track.bpm) {
      const [min, max] = selectedBpmRange === '180+' 
        ? [180, 999] 
        : selectedBpmRange.split('-').map(Number);
      if (track.bpm < min || track.bpm > max) return false;
    }

    // Price filter
    if (selectedPrice !== 'All') {
      const price = track.price || 0;
      const isFree = track.is_free || price === 0;
      if (selectedPrice === 'Free' && !isFree) return false;
      if (selectedPrice === 'Under $25' && (isFree || price >= 25)) return false;
      if (selectedPrice === '$25-$50' && (isFree || price < 25 || price > 50)) return false;
      if (selectedPrice === '$50-$100' && (price < 50 || price > 100)) return false;
      if (selectedPrice === 'Over $100' && price <= 100) return false;
    }
    
    return true;
  }).sort((a, b) => {
    if (selectedDateSort === 'Newest First') {
      const dateA = a.published_date ? new Date(a.published_date + 'T12:00:00') : new Date(0);
      const dateB = b.published_date ? new Date(b.published_date + 'T12:00:00') : new Date(0);
      return dateB - dateA;
    }
    if (selectedDateSort === 'Oldest First') {
      const dateA = a.published_date ? new Date(a.published_date + 'T12:00:00') : new Date(0);
      const dateB = b.published_date ? new Date(b.published_date + 'T12:00:00') : new Date(0);
      return dateA - dateB;
    }
    return 0;
  });

  const activeFiltersCount = [selectedGenre, selectedMood, selectedBpmRange].filter(f => f !== 'All').length
    + (selectedPrice !== 'All' ? 1 : 0)
    + (selectedDateSort !== 'Default' ? 1 : 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading beats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Toaster position="top-center" theme="dark" />
      
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onEnded={playNext}
        onLoadedMetadata={handleTimeUpdate}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-black border-b border-white/30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">
                Music Store
              </h1>
              <p className="text-sm text-gray-400">{tracks.length} tracks available</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={copyShareLink}
                className="px-4 py-2 bg-black border border-white/30 hover:border-white/50 rounded-lg transition-colors flex items-center gap-2 text-sm"
              >
                {linkCopied ? <Check size={16} className="text-green-400" /> : <Share2 size={16} />}
                {linkCopied ? 'Copied!' : 'Share'}
              </button>
              
              <button
                onClick={() => setShowCart(true)}
                className="relative px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <ShoppingCart size={16} />
                Cart
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search beats by name, artist, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black border border-white/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 p-1 bg-black border border-white/30 rounded-xl" data-testid="beatstore-view-toggle">
            <button
              onClick={() => { setViewMode('list'); localStorage.setItem('beatStoreViewMode', 'list'); }}
              className={`p-2.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
              title="List View"
              data-testid="beatstore-view-list-btn"
            >
              <List size={18} />
            </button>
            <button
              onClick={() => { setViewMode('grid'); localStorage.setItem('beatStoreViewMode', 'grid'); }}
              className={`p-2.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
              title="Grid View"
              data-testid="beatstore-view-grid-btn"
            >
              <LayoutGrid size={18} />
            </button>
          </div>
          
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 rounded-xl border transition-colors flex items-center gap-2 ${
              showFilters || activeFiltersCount > 0
                ? 'bg-purple-600 border-purple-600 text-white'
                : 'bg-black border-white/30 text-gray-300 hover:border-white/50'
            }`}
          >
            <Filter size={18} />
            Filters
            {activeFiltersCount > 0 && (
              <span className="w-5 h-5 bg-white text-purple-600 rounded-full text-xs flex items-center justify-center font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 p-4 bg-black rounded-xl border border-white/30 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Genre</label>
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-white/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                {genres.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Mood</label>
              <select
                value={selectedMood}
                onChange={(e) => setSelectedMood(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-white/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                {moods.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">BPM Range</label>
              <select
                value={selectedBpmRange}
                onChange={(e) => setSelectedBpmRange(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-white/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                {bpmRanges.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Price</label>
              <select
                value={selectedPrice}
                onChange={(e) => setSelectedPrice(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-white/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                {priceRanges.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Sort by Date</label>
              <select
                value={selectedDateSort}
                onChange={(e) => setSelectedDateSort(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-white/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                {dateSorts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Track List / Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-32">
        {filteredTracks.length === 0 ? (
          <div className="text-center py-20">
            <Music size={64} className="mx-auto text-gray-700 mb-4" />
            <h3 className="text-xl text-gray-400">No tracks found</h3>
            <p className="text-gray-500 mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            {/* ========== LIST VIEW ========== */}
            {viewMode === 'list' && (
              <div className="space-y-2" data-testid="beatstore-list-view">
                {filteredTracks.map((track, index) => (
                  <div
                    key={track.id}
                    className={`group rounded-xl p-3 sm:p-4 transition-all duration-300 ${
                      currentTrack?.id === track.id
                        ? 'bg-purple-900/30 border border-purple-500'
                        : 'bg-black border border-white/30 hover:border-white/50'
                    }`}
                  >
                    {/* Mobile Layout */}
                    <div className="flex sm:hidden items-start gap-3">
                      {/* Play Button & Cover */}
                      <button
                        onClick={() => playTrack(track)}
                        className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-black border border-white/20"
                      >
                        {track.cover_url ? (
                          <img
                            src={`${API_URL}${track.cover_url}`}
                            alt={track.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                            <Music size={20} className="text-white/80" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          {currentTrack?.id === track.id && isPlaying ? (
                            <Pause size={18} className="text-white" />
                          ) : (
                            <Play size={18} className="text-white ml-0.5" fill="white" />
                          )}
                        </div>
                      </button>

                      {/* Track Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-sm truncate">{track.title}</h3>
                        <p className="text-xs text-gray-400 truncate">{track.artist || 'Unknown Artist'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {track.bpm && <span className="text-xs text-gray-500">{track.bpm} BPM</span>}
                          {track.key && <span className="text-xs text-gray-500">• {track.key}</span>}
                        </div>
                      </div>

                      {/* Price & Cart */}
                      <div className="flex flex-col items-end gap-2">
                        {track.is_free ? (
                          <>
                            <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-medium">FREE</span>
                            <button
                              onClick={() => downloadTrack(track)}
                              className="p-1.5 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                              title="Download"
                            >
                              <Download size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="px-2 py-1 bg-black border border-white/30 text-white rounded text-xs font-medium">${track.price?.toFixed(2) || '0.00'}</span>
                            <button
                              onClick={() => addToCart(track)}
                              className="p-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                              title="Add to cart"
                            >
                              <ShoppingCart size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center gap-4">
                      {/* Play Button & Cover */}
                      <button
                        onClick={() => playTrack(track)}
                        className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 group/play bg-black border border-white/20"
                      >
                        {track.cover_url ? (
                          <img
                            src={`${API_URL}${track.cover_url}`}
                            alt={track.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                            <Music size={24} className="text-white/80" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover/play:opacity-100 transition-opacity">
                          {currentTrack?.id === track.id && isPlaying ? (
                            <Pause size={24} className="text-white" />
                          ) : (
                            <Play size={24} className="text-white" fill="white" />
                          )}
                        </div>
                      </button>

                      {/* Track Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate">{track.title}</h3>
                        <p className="text-sm text-gray-400 truncate">{track.artist || 'Unknown Artist'}</p>
                      </div>

                      {/* Metadata */}
                      <div className="hidden md:flex items-center gap-3 text-sm text-gray-400">
                        {track.bpm && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-black border border-white/30 rounded">
                            <Activity size={12} />
                            {track.bpm}
                          </span>
                        )}
                        {track.key && (
                          <span className="px-2 py-1 bg-black border border-white/30 rounded text-xs">{track.key}</span>
                        )}
                        {track.genre && (
                          <span className="px-2 py-1 bg-purple-900/50 text-purple-300 rounded text-xs">{track.genre}</span>
                        )}
                      </div>

                      {/* Price & Actions */}
                      <div className="flex items-center gap-2">
                        {track.is_free ? (
                          <>
                            <span className="px-3 py-1.5 bg-green-600/20 text-green-400 rounded-lg text-sm font-medium">
                              FREE
                            </span>
                            <button
                              onClick={() => downloadTrack(track)}
                              className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                              title="Download free beat"
                            >
                              <Download size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="px-3 py-1.5 bg-black border border-white/30 text-white rounded-lg text-sm font-medium flex items-center gap-1">
                              <DollarSign size={14} />
                              {track.price?.toFixed(2) || '0.00'}
                            </span>
                            <button
                              onClick={() => addToCart(track)}
                              className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                              title="Add to cart"
                            >
                              <ShoppingCart size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ========== GRID VIEW ========== */}
            {viewMode === 'grid' && (
              <div 
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4" 
                data-testid="beatstore-grid-view"
              >
                {filteredTracks.map((track, index) => {
                  const isCurrentTrack = currentTrack?.id === track.id;
                  const isTrackPlaying = currentTrack?.id === track.id && isPlaying;
                  
                  return (
                    <div
                      key={track.id}
                      data-testid={`beatstore-grid-card-${track.id}`}
                      className="group cursor-pointer"
                    >
                      {/* Album Art with Play Button Overlay */}
                      <div 
                        className="relative aspect-square rounded-lg overflow-hidden mb-2 sm:mb-3 bg-black border border-white/20"
                        onClick={() => playTrack(track)}
                      >
                        {track.cover_url ? (
                          <img
                            src={`${API_URL}${track.cover_url}`}
                            alt={track.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                            <Music size={32} className="text-white/60 sm:w-10 sm:h-10" />
                          </div>
                        )}
                        
                        {/* Play Button Overlay - Bottom Right */}
                        <div className={`absolute bottom-1.5 sm:bottom-2 right-1.5 sm:right-2 transition-all duration-200 ${isTrackPlaying ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}`}>
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-xl bg-purple-600`}>
                            {isTrackPlaying ? (
                              <Pause size={20} className="text-white sm:w-6 sm:h-6" />
                            ) : (
                              <Play size={20} className="text-white ml-0.5 sm:w-6 sm:h-6 sm:ml-1" fill="white" />
                            )}
                          </div>
                        </div>
                        
                        {/* Now Playing Indicator */}
                        {isTrackPlaying && (
                          <div className="absolute top-2 left-2 flex items-center gap-0.5 bg-purple-600 px-2 py-1 rounded-full">
                            <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" />
                            <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                            <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                          </div>
                        )}

                        {/* Price Badge */}
                        {track.is_free ? (
                          <div className="absolute top-2 right-2 px-2 py-1 bg-green-600/90 rounded text-xs font-semibold text-white">
                            FREE
                          </div>
                        ) : track.price ? (
                          <div className="absolute top-2 right-2 px-2 py-1 bg-black/90 border border-white/30 rounded text-xs font-semibold text-white">
                            ${track.price.toFixed(2)}
                          </div>
                        ) : null}
                      </div>
                      
                      {/* Track Info */}
                      <h4 className={`font-medium text-xs sm:text-sm truncate mb-0.5 sm:mb-1 ${isCurrentTrack ? 'text-purple-400' : 'text-white'}`}>
                        {track.title}
                      </h4>
                      <p className="text-gray-400 text-xs truncate">
                        {track.artist || 'Unknown Artist'}
                      </p>
                      
                      {/* Track metadata */}
                      <div className="flex items-center justify-between mt-1 sm:mt-2">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          {track.bpm && <span>{track.bpm} BPM</span>}
                          {track.key && <span className="hidden sm:inline">• {track.key}</span>}
                        </div>
                        {track.is_free ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadTrack(track); }}
                            className="p-1.5 bg-green-600/80 hover:bg-green-600 rounded-lg transition-colors"
                            title="Download free beat"
                          >
                            <Download size={12} />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); addToCart(track); }}
                            className="p-1.5 bg-purple-600/80 hover:bg-purple-600 rounded-lg transition-colors"
                            title="Add to cart"
                          >
                            <ShoppingCart size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Fixed Player Bar */}
      {currentTrack && (
        <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-white/30 z-50">
          {/* Progress Bar */}
          <div
            className="h-1 bg-gray-800 cursor-pointer"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-purple-500"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              {/* Track Info */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-black border border-white/20">
                  {currentTrack.cover_url ? (
                    <img
                      src={`${API_URL}${currentTrack.cover_url}`}
                      alt={currentTrack.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                      <Music size={20} className="text-white/80" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-white truncate">{currentTrack.title}</p>
                  <p className="text-sm text-gray-400 truncate">{currentTrack.artist}</p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-3">
                <button onClick={playPrevious} className="p-2 text-gray-400 hover:text-white transition-colors">
                  <SkipBack size={20} />
                </button>
                <button
                  onClick={() => {
                    if (isPlaying) {
                      audioRef.current?.pause();
                    } else {
                      audioRef.current?.play();
                    }
                    setIsPlaying(!isPlaying);
                  }}
                  className="w-12 h-12 bg-purple-600 hover:bg-purple-700 rounded-full flex items-center justify-center transition-colors"
                >
                  {isPlaying ? <Pause size={24} /> : <Play size={24} fill="white" />}
                </button>
                <button onClick={playNext} className="p-2 text-gray-400 hover:text-white transition-colors">
                  <SkipForward size={20} />
                </button>
              </div>

              {/* Time & Volume */}
              <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
                <span className="text-sm text-gray-400">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                  >
                    {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => {
                      const newVol = parseFloat(e.target.value);
                      setVolume(newVol);
                      setIsMuted(newVol === 0);
                      if (audioRef.current) {
                        audioRef.current.volume = newVol;
                      }
                    }}
                    className="w-24 accent-purple-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowCart(false)} />
          <div className="relative w-full max-w-md bg-black border-l border-white/30 h-full overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Your Cart</h2>
                <button onClick={() => setShowCart(false)} className="p-2 text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart size={48} className="mx-auto text-gray-700 mb-4" />
                  <p className="text-gray-400">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto">
                    {cart.map(track => (
                      <div key={track.id} className="flex items-center gap-3 p-3 bg-black border border-white/30 rounded-lg">
                        <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-black border border-white/20">
                          {track.cover_url ? (
                            <img 
                              src={`${API_URL}${track.cover_url}`} 
                              alt="" 
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="w-full h-full bg-purple-600 flex items-center justify-center">
                              <Music size={16} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{track.title}</p>
                          <p className="text-sm text-gray-400">
                            {track.is_free || !track.price ? (
                              <span className="text-green-400">FREE</span>
                            ) : (
                              `$${track.price?.toFixed(2)}`
                            )}
                          </p>
                        </div>
                        {/* Download button for individual track */}
                        <button
                          onClick={() => downloadTrack(track)}
                          className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => removeFromCart(track.id)}
                          className="p-2 text-gray-400 hover:text-red-400"
                          title="Remove"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-white/30 pt-4 space-y-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className={getCartTotal() === 0 ? 'text-green-400' : ''}>
                        {getCartTotal() === 0 ? 'FREE' : `$${getCartTotal().toFixed(2)}`}
                      </span>
                    </div>
                    
                    {/* Terms and Conditions Agreement */}
                    <div className="bg-black/50 border border-white/20 rounded-lg p-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                          className="mt-1 w-4 h-4 rounded border-white/30 bg-black text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                          data-testid="terms-checkbox"
                        />
                        <span className="text-xs text-gray-300">
                          I agree to the{' '}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowTermsModal(true);
                            }}
                            className="text-purple-400 hover:text-purple-300 underline"
                          >
                            Terms & Conditions
                          </button>
                          {' '}for beat licensing and usage
                        </span>
                      </label>
                    </div>
                    
                    {/* Checkout/Download All Button */}
                    <button 
                      onClick={handleCheckout}
                      disabled={checkoutLoading || !agreedToTerms}
                      className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      {checkoutLoading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Processing...
                        </>
                      ) : getCartTotal() === 0 ? (
                        <>
                          <Download size={18} />
                          Download All Free Beats
                        </>
                      ) : (
                        <>
                          <ShoppingCart size={18} />
                          Checkout ${getCartTotal().toFixed(2)}
                        </>
                      )}
                    </button>
                    
                    {!agreedToTerms && (
                      <p className="text-xs text-yellow-500 text-center">
                        Please agree to the Terms & Conditions to proceed
                      </p>
                    )}
                    
                    {/* Info text */}
                    <p className="text-xs text-gray-500 text-center">
                      {getCartTotal() === 0 
                        ? 'All tracks in your cart are free!' 
                        : 'Secure checkout powered by Stripe'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-black border border-white/30 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Beat Store Terms & Conditions</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)] text-sm text-gray-300 space-y-6">
              <section>
                <h3 className="text-white font-semibold mb-2">1. Overview</h3>
                <p>This website and beat store is owned and operated by CREATR LLC. By purchasing, downloading, or using any music, beats, or audio files from this platform, you agree to these Terms & Conditions.</p>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">2. Ownership & Copyright</h3>
                <p className="mb-2">All beats, instrumentals, audio files, and compositions remain the exclusive intellectual property of CREATR LLC and/or its licensors.</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Purchasing a beat does NOT transfer ownership</li>
                  <li>You are granted a license to use the beat, not ownership of the beat itself</li>
                  <li>CREATR retains full rights to:
                    <ul className="list-disc pl-5 mt-1">
                      <li>Sell the beat to other customers (unless exclusive rights are purchased)</li>
                      <li>Register the beat with copyright agencies</li>
                      <li>Monetize the beat in any form</li>
                    </ul>
                  </li>
                </ul>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">3. License Types</h3>
                <div className="mb-3">
                  <h4 className="text-purple-400 font-medium mb-1">Non-Exclusive License (Standard Purchase)</h4>
                  <p className="mb-2">When you purchase a beat without exclusivity:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>You receive a non-exclusive, non-transferable license</li>
                    <li>Multiple users may purchase and use the same beat</li>
                    <li>You may: Record vocals over the beat, distribute your song on platforms like Spotify, Apple Music, etc., use for commercial purposes within limits</li>
                    <li>You may NOT: Claim ownership of the beat itself, resell, lease, sublicense, or distribute the beat alone, upload the beat to content ID systems (unless allowed)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-purple-400 font-medium mb-1">Exclusive License (If Offered)</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>You receive exclusive commercial rights to the beat</li>
                    <li>The beat will be removed from public sale</li>
                    <li>Previous buyers retain their licenses</li>
                    <li>CREATR still retains underlying copyright ownership and credit as producer (unless otherwise agreed)</li>
                  </ul>
                </div>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">4. Payment Terms</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>All sales are final</li>
                  <li>No refunds after purchase due to the digital nature of the product</li>
                  <li>Prices are subject to change at any time</li>
                  <li>Unauthorized chargebacks may result in license termination and legal action</li>
                </ul>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">5. Delivery of Files</h3>
                <p className="mb-2">Upon purchase, you will receive:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Digital download of the beat (MP3, WAV, or stems depending on license)</li>
                  <li>License agreement document</li>
                </ul>
                <p className="mt-2 text-yellow-400">You are responsible for downloading and storing your files.</p>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">6. Credit Requirement</h3>
                <p>You must credit the producer as: <span className="text-purple-400 font-medium">"Produced by CREATR"</span> (or specific producer name if listed)</p>
                <p className="text-yellow-400 mt-1">Failure to provide credit may result in license termination.</p>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">7. Content ID & Royalties</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>CREATR reserves the right to register beats with platforms like YouTube Content ID unless exclusive rights are purchased</li>
                  <li>Royalty splits may apply depending on license type</li>
                  <li>Publishing and mechanical royalties remain with CREATR unless otherwise agreed</li>
                </ul>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">8. Prohibited Uses</h3>
                <p className="mb-2">You may NOT use beats for:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Illegal or defamatory content</li>
                  <li>Hate speech or harmful material</li>
                  <li>AI training datasets without permission</li>
                  <li>Resale as standalone audio product</li>
                </ul>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">9. Termination</h3>
                <p className="mb-2">CREATR reserves the right to terminate your license if:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Terms are violated</li>
                  <li>Unauthorized use is detected</li>
                  <li>Payment disputes or fraud occur</li>
                </ul>
                <p className="text-red-400 mt-2">Upon termination, all rights to use the beat are revoked.</p>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">10. Limitation of Liability</h3>
                <p className="mb-2">CREATR is not liable for:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Loss of revenue or profits</li>
                  <li>Platform takedowns or copyright claims due to misuse</li>
                  <li>Third-party disputes</li>
                </ul>
                <p className="mt-2">Use of the beat is at your own risk.</p>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">11. Governing Law</h3>
                <p>These Terms are governed by the laws of the State of Texas, United States.</p>
              </section>
              
              <section>
                <h3 className="text-white font-semibold mb-2">12. Contact</h3>
                <p>For licensing questions or disputes:</p>
                <p className="mt-2">Email: <a href="mailto:creatrverse@gmail.com" className="text-purple-400 hover:text-purple-300">creatrverse@gmail.com</a></p>
                <p>Company: CREATR LLC</p>
              </section>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-white/20 flex items-center justify-between gap-4">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setAgreedToTerms(true);
                  setShowTermsModal(false);
                }}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <Check size={16} />
                I Agree to Terms
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicBeatStore;
